import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { http } from "../../axios/axios";
import {
  ArrowLeft,
  ChevronRight,
  CircleDashed,
  ArrowRight,
  Phone,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import RelatedProducts from "../RelatedProducts";
import TrendingItems from "../home/TrandingItems";
import { CheckCircle, Download } from "lucide-react";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import { Helmet } from "react-helmet-async";
import { ProductReviews } from "../ProductReviews";
import {
  buildProductWhatsAppMessage,
  getProductCanonicalUrl,
  getProductOgImage,
} from "../../utils/whatsappMessage";

// const API_BASE = import.meta.env.API_BASE || "https://api.suraksha24x7.com";
const API_BASE = "http://localhost:8080";

// Existing Suraksha 24X7 contact number, also used in Header/Footer/WhatsApp float icon.
const CONTACT_PHONE_NUMBER = "8448341881";
const CONTACT_WHATSAPP_NUMBER = "918448341881";
const DESCRIPTION_CHAR_LIMIT = 220;

export default function ProductDetails() {
  const { id, productSlug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeImage, setActiveImage] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const slug = productSlug || id;

  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      setError("");
      setProduct(null);
      try {
        const response = await http.get(`/products/${slug}`);
        const data = response.data?.product || response.data;
        if (!data || typeof data !== "object") {
          throw new Error("Product details were not returned by the API.");
        }
        setProduct(data);
        setActiveImage(data?.images?.[0]);
        setShowFullDescription(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Failed to load product details:", error);
        setError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load product details.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
    if (slug) fetchProductDetails();
    else {
      setError("Product not found.");
      setLoading(false);
    }
  }, [slug]);

  const getYoutubeId = (url) => {
    if (!url) return null;
    const regExp = /(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const handleOpenServiceRequest = () => {
    navigate("/complain", { state: { selectedProduct: product } });
  };

  const description = product?.description || "";
  const isItProduct =
    product?.catalogType === "ip-network" ||
    Boolean(product?.ipNetworkCategory) ||
    Boolean(product?.ipNetworkSubCategory);
  const isLongDescription = description.length > DESCRIPTION_CHAR_LIMIT;
  const displayedDescription =
    showFullDescription || !isLongDescription
      ? description
      : `${description.slice(0, DESCRIPTION_CHAR_LIMIT).trimEnd()}...`;
  const productTitle = product?.metaTitle || product?.title || "Product Details";
  const productDescription =
    product?.metaDescription || product?.description || "View product details.";
  const productUrl = getProductCanonicalUrl(product);
  const productOgImage = getProductOgImage(product);
  const whatsappMessage = buildProductWhatsAppMessage({
    product,
    enquiryType: "Product Enquiry",
  });
  const whatsappUrl = `https://wa.me/${CONTACT_WHATSAPP_NUMBER}?text=${encodeURIComponent(
    whatsappMessage,
  )}`;

  if (loading) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <CircleDashed className="animate-spin text-blue-600 mb-4" size={42} />
        <p className="text-[11px] font-semibold tracking-[0.3em] text-slate-400 uppercase">
          Loading Product Details
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-slate-600">{error}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-slate-600">Product not found.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{productTitle}</title>

        <meta name="description" content={productDescription} />

        <meta
          name="keywords"
          content={product?.metaTags?.length ? product.metaTags.join(", ") : ""}
        />
        <link rel="canonical" href={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={productTitle} />
        <meta property="og:description" content={productDescription} />
        <meta property="og:image" content={productOgImage} />
        <meta property="og:url" content={productUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={productTitle} />
        <meta name="twitter:description" content={productDescription} />
        <meta name="twitter:image" content={productOgImage} />

        {product?.schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: product.schemaScript,
            }}
          />
        )}
      </Helmet>
      <div className="min-h-screen bg-[#f6f7fb] text-slate-900">
        <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
          <div
            className="
      w-full
      px-4
      sm:px-6
      md:px-10
      lg:px-14
      xl:px-20
      2xl:px-28
      py-4
      flex
      justify-between
      items-center
    "
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition"
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <div className="hidden md:flex text-xs text-slate-400 gap-2">
              <span>{product?.brand?.brandName}</span>
              <ChevronRight size={12} />
              <span>{product?.category?.name}</span>
            </div>
          </div>
        </nav>

        <main
          className="
 w-full
 px-4
 sm:px-6
 md:px-10
 lg:px-14
 xl:px-20
 2xl:px-28
 py-8
 sm:py-12
 space-y-10
 sm:space-y-14
 "
        >
          <div
            className="
grid
grid-cols-1
xl:grid-cols-[1.1fr_0.9fr]
gap-8
lg:gap-12
2xl:gap-20
items-start
"
          >
            <div className="space-y-4 sm:space-y-5 w-full">
              <div
                className="
flex
flex-col
lg:flex-row
gap-3
lg:gap-5
w-full
"
              >
                {/* THUMBNAILS */}
                <div
                  className="
order-2
lg:order-1
flex
lg:flex-col
gap-3
overflow-x-auto
lg:overflow-visible
pb-2
lg:pb-0
lg:w-20
xl:w-24
"
                >
                  {(product?.images || [])
                    .filter(Boolean)
                    .slice(0, 5)
                    .map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImage(img)}
                        className={`
        w-10
h-10
sm:w-12
sm:h-12
lg:w-18
lg:h-18
xl:w-22
xl:h-22
          rounded
          bg-white/70
          backdrop-blur
          flex
          items-center
          justify-center
          transition
          border
          flex-shrink-0

          ${
            activeImage === img
              ? "border-blue-600 shadow-md scale-105"
              : "border-transparent opacity-60 hover:opacity-100"
          }
        `}
                      >
                        <img
                          src={GetImageUrl(img.url)}
                          alt={`${product?.title} product image`}
                          className="object-contain p-1 w-full h-full"
                        />
                      </button>
                    ))}
                </div>
                {/* MAIN IMAGE */}
                {activeImage?.url ? (
                  <div
                    className="
      order-1
      lg:order-2
      relative
      w-full
      h-[260px]
      sm:h-[300px]
      md:h-[360px]
      lg:h-[480px]
      rounded
      overflow-hidden
      bg-white/60
      backdrop-blur-xl
      p-2
      sm:p-3
      md:p-4
      lg:p-6
      shadow-sm
    "
                  >
                    <img
                      src={GetImageUrl(activeImage.url)}
                      alt={`${product?.title} product image`}
                      onError={(e) => {
                        e.currentTarget.src = "/fallback-product.png";
                      }}
                      className="
        w-full
        h-full
        object-contain
        hover:scale-105
        transition
        duration-500
      "
                    />

                    <div
                      className="
        absolute
        top-2
        left-2
        sm:top-4
        sm:left-4
        bg-black
        text-white
        text-[9px]
        sm:text-[11px]
        px-2
        sm:px-3
        py-1
        rounded-full
      "
                    >
                      Verified Product
                    </div>
                  </div>
                ) : (
                  <div
                    className="
      order-1
      lg:order-2
      w-full
      h-[260px]
      sm:h-[300px]
      md:h-[360px]
      lg:h-[480px]
      rounded
      bg-white
      flex
      items-center
      justify-center
      text-slate-400
      shadow-sm
    "
                  >
                    <div className="text-center">
                      <p className="text-sm font-medium">
                        Product image unavailable
                      </p>
                      <p className="text-xs mt-1">Please try again later</p>
                    </div>
                  </div>
                )}
              </div>

              {/* WARRANTY + CATEGORY */}
              {(product?.warrantyDuration || product?.category?.name) && (
                <div
                  className="
      grid
      grid-cols-1
      sm:grid-cols-2
      gap-4
      max-w-[520px]
      mx-auto
      lg:mx-0
    "
                >
                  {product?.warrantyDuration && (
                    <div
                      className="
          group
          relative
          overflow-hidden
          bg-white
          rounded-2xl
          p-5
          border
          border-slate-200
          shadow-sm
          hover:shadow-lg
          transition-all
          duration-300
        "
                    >
                      <div
                        className="
            absolute
            inset-0
            bg-gradient-to-br
            from-blue-50
            to-transparent
            opacity-70
          "
                      />

                      <div className="relative">
                        <p
                          className="
              text-[10px]
              sm:text-[11px]
              uppercase
              tracking-[0.2em]
              text-slate-400
              font-semibold
            "
                        >
                          Warranty
                        </p>

                        <p
                          className="
              mt-2
              text-xl
              font-bold
              text-slate-900
            "
                        >
                          {product.warrantyDuration}
                          <span className="text-sm ml-1 text-slate-500 font-medium">
                            {product.warrantyUnit}
                          </span>
                        </p>

                        <div
                          className="
              mt-3
              w-10
              h-1
              rounded-full
              bg-blue-600
            "
                        />
                      </div>
                    </div>
                  )}

                  {product?.category?.name && (
                    <div
                      className="
          group
          relative
          overflow-hidden
          bg-white
          rounded-2xl
          p-5
          border
          border-slate-200
          shadow-sm
          hover:shadow-lg
          transition-all
          duration-300
        "
                    >
                      <div
                        className="
            absolute
            inset-0
            bg-gradient-to-br
            from-indigo-50
            to-transparent
            opacity-70
          "
                      />

                      <div className="relative">
                        <p
                          className="
              text-[10px]
              sm:text-[11px]
              uppercase
              tracking-[0.2em]
              text-slate-400
              font-semibold
            "
                        >
                          Category
                        </p>

                        <p
                          className="
              mt-2
              text-base
              sm:text-lg
              font-bold
              text-slate-900
              line-clamp-2
            "
                        >
                          {product.category.name}
                        </p>

                        <div
                          className="
              mt-3
              w-10
              h-1
              rounded-full
              bg-indigo-600
            "
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col justify-center space-y-6">
              {(product?.brand?.brandName ||
                product?.ipNetworkBrand?.brandName ||
                product?.title ||
                product?.description) && (
                <div>
                  {(product?.brand?.brandName || product?.ipNetworkBrand?.brandName) && (
                    <p className="text-sm font-medium text-slate-500">
                      Brand: {product.brand?.brandName || product.ipNetworkBrand?.brandName}
                    </p>
                  )}

                  {product?.assetId && (
                    <p className="mt-2 text-sm font-medium text-slate-500">
                      Asset ID:
                      <span className="ml-2 text-slate-900 font-semibold">
                        {product.assetId}
                      </span>
                    </p>
                  )}

                  {product?.title && (
                    <h1
                      className="
    mt-4
    text-xl
    sm:text-2xl
    md:text-3xl
    lg:text-4xl
    xl:text-5xl
    font-semibold
    leading-tight
    tracking-tight
  "
                    >
                      {product.title}
                    </h1>
                  )}

                  {product?.description && (
                    <div className="mt-4">
                      <p
                        className="
        text-slate-600
        text-sm
        sm:text-base
        xl:text-lg
        leading-relaxed
        whitespace-pre-wrap
      "
                      >
                        {displayedDescription}
                      </p>

                      {isLongDescription && (
                        <button
                          type="button"
                          onClick={() =>
                            setShowFullDescription((prev) => !prev)
                          }
                          className="mt-1 text-sm font-semibold text-blue-600 hover:text-blue-700 transition"
                        >
                          {showFullDescription ? "See Less" : "See More"}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <a
                      href={`tel:${CONTACT_PHONE_NUMBER}`}
                      className="
        inline-flex
        items-center
        justify-center
        gap-2
        px-5
        py-3
        rounded
        bg-blue-600
        hover:bg-blue-700
        text-white
        text-sm
        font-semibold
        transition
        "
                    >
                      <Phone size={16} />
                      Call Now
                    </a>

                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="
        inline-flex
        items-center
        justify-center
        gap-2
        px-5
        py-3
        rounded
        bg-[#25D366]
        hover:bg-[#1ebe5b]
        text-white
        text-sm
        font-semibold
        transition
        "
                    >
                      <FaWhatsapp size={16} />
                      WhatsApp Now
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
          {product?.features?.filter(Boolean)?.length > 0 && (
            <div className="bg-white/70 backdrop-blur p-4 sm:p-6 rounded shadow-sm">
              <h2 className="text-xs sm:text-sm font-semibold text-slate-700 uppercase tracking-widest mb-4">
                Features
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-x-6 sm:gap-y-3">
                {product.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2 sm:gap-3">
                    <CheckCircle
                      className="text-green-500 mt-0.5 flex-shrink-0"
                      size={14}
                    />
                    <p className="text-xs sm:text-sm text-slate-600 leading-snug break-words">
                      {feature}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}{" "}
          {product?.pdfs?.filter(Boolean)?.length > 0 && (
            <div className="mt-10 sm:mt-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight mb-5 sm:mb-6">
                Download Documents
              </h2>

              <div className="grid gap-3 sm:gap-4">
                {product.pdfs.map((pdf, idx) => (
                  <a
                    key={idx}
                    href={GetImageUrl(pdf.url)}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-3
          bg-white/70
          backdrop-blur
          border
          border-white/40
          rounded
          p-4
          sm:p-5
          hover:shadow-md
          transition
          "
                  >
                    <span className="text-sm font-medium text-slate-700 truncate">
                      {pdf.name}
                    </span>

                    <div className="flex items-center gap-2">
                      <Download className="text-blue-600" size={18} />

                      <span className="text-xs text-slate-400">Download</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
          {product?.specifications?.some(
            (spec) => spec?.title || spec?.details,
          ) && (
            <div className="mt-10 w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight mb-5 sm:mb-6">
                Product Specifications
              </h2>

              <div className="relative w-full rounded bg-white/70 backdrop-blur border border-white/40 shadow-sm overflow-hidden">
                <div className="hidden sm:grid grid-cols-[40%_60%] bg-slate-50/70 text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-white/40">
                  <div className="px-4 sm:px-6 py-3 sm:py-4">Specification</div>
                  <div className="px-4 sm:px-6 py-3 sm:py-4">Details</div>
                </div>

                <div className="divide-y divide-white/60">
                  {product?.specifications
                    ?.filter((spec) => spec?.title || spec?.details)
                    .map((spec, idx) => (
                      <div
                        key={idx}
                        className="p-4 sm:px-6 sm:py-4 hover:bg-white/80 transition flex flex-col sm:grid sm:grid-cols-[40%_60%] gap-2 sm:gap-0"
                      >
                        <div className="text-sm font-medium text-slate-800 break-words">
                          {spec?.title || "-"}
                        </div>

                        <div className="text-sm text-slate-500 leading-relaxed break-words">
                          {spec?.details || "-"}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
          {product?.youtubeLink?.length > 0 && (
            <div className="mt-12">
              <h2
                className="
      text-xl
      sm:text-2xl
      md:text-3xl
      font-semibold
      tracking-tight
      mb-6
    "
              >
                Product Videos
              </h2>

              <div
                className="
      grid
      grid-cols-1
      sm:grid-cols-2
      xl:grid-cols-4
      gap-5
      "
              >
                {product.youtubeLink.map((url, idx) => {
                  const videoId = getYoutubeId(url);

                  if (!videoId) return null;

                  return (
                    <div
                      key={idx}
                      className="
            bg-white/70
            backdrop-blur
            rounded-2xl
            overflow-hidden
            shadow-sm
            border
            border-white/40
            "
                    >
                      <div
                        className="
              aspect-video
              w-full
              "
                      >
                        <iframe
                          className="
                w-full
                h-full
                "
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title="Product Video"
                          allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture
            "
                          allowFullScreen
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {(isItProduct ? product?.ipNetworkCategory?._id : product?.category?._id) && product?._id && (
            <RelatedProducts
              categoryId={isItProduct ? product.ipNetworkCategory?._id : product.category?._id}
              catalogType={isItProduct ? "ip-network" : "catalog"}
              currentProductId={product._id}
            />
          )}
          <TrendingItems type={isItProduct ? "ip-network" : "catalog"} />
          <ProductReviews
            productId={product?._id}
          />
          <div className="relative w-full rounded  overflow-hidden mt-16">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800" />
            <div className="absolute -top-24 -right-24 w-80 h-80 bg-blue-500/10 blur-3xl rounded-full" />
            <div className="absolute -bottom-28 -left-28 w-80 h-80 bg-indigo-500/10 blur-3xl rounded-full" />

            <div className="absolute inset-0 ring-1 ring-white/10 rounded" />

            <div className="relative z-10 px-6 md:px-14 py-14 flex flex-col items-center text-center">
  <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">
    Technical Support
  </h2>

  <p className="mt-3 text-sm md:text-base text-slate-400 max-w-lg leading-relaxed">
    Log a service request or deployment ticket for this asset.
  </p>

  <Link
    to="/contact"
    className="mt-6 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-3 px-5 rounded transition"
  >
    Open Service Request
    <ArrowRight size={18} />
  </Link>
</div>
          </div>
        </main>
      </div>
    </>
  );
}
